/* eslint-disable react/forbid-prop-types */
import { func, object, string } from 'prop-types'
import { useState, useEffect } from 'react'
import Icon from '../../icon/index.js'
import { addSingleQueryParam, addQueryParams } from '../../../utilities/queryParams.js'
import '../../../../styles/components/_mediaUploaderIframe.scss'
import buildPublicFormQuery from '../../../utilities/buildPublicFormQuery.js'

function MediaUploaderIframe({
  feedKey,
  handleCloseClick,
  language,
  termsAndConditionsURL,
  privacyPolicyUrl,
  settings,
}) {
  const [formData, setFormData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const { basic_fields, forms, styling, showcase_url } = settings || {}
  const { logo } = styling || {}

  // If we have styling field it means that it is version 2
  let url = styling ? __MEDIA_UPLOADER_IFRAME__ : __MEDIA_UPLOADER_IFRAME_V1__

  if (feedKey && language) {
    const params = {
      setLng: language,
      feed_key: feedKey,
    }
    url = addQueryParams(url, params)
  }

  if (termsAndConditionsURL) {
    url = addSingleQueryParam(url, 'toc', termsAndConditionsURL)
  }
  if (privacyPolicyUrl) {
    url = addSingleQueryParam(url, 'pp', privacyPolicyUrl)
  }

  if (formData) {
    url = addSingleQueryParam(url, 'enable_forms', forms?.enable_forms)
  }

  const sendPostMessage = (iframe) => {
    if (iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          msg: 'setData',
          media_uploader_settings_v2: {
            formData,
            basic_fields,
            settings: {
              showcase_url,
              logo,
              enable_forms: forms?.enable_forms,
            },
          },
        },
        '*',
      )
    }
  }

  useEffect(() => {
    if (forms?.enable_forms && forms.form_id) {
      const fetchFormData = async () => {
        setIsLoading(true)
        try {
          const urlForm = buildPublicFormQuery(
            __EXPERIENCE_ENDPOINT__,
            forms.external_form_id,
            language,
          )
          const response = await fetch(urlForm)
          // This causes it to enter the catch, because the fetch only catches errors if it is a network or 400 error
          if (!response.ok) {
            throw new Error(`Error fetching form data: ${response.status}`)
          }
          const data = await response.json()
          setFormData(data)
        } catch {
          setFormData(null)
        } finally {
          setIsLoading(false)
        }
      }

      fetchFormData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const spinnerStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    border: '4px solid rgba(0, 0, 0, 0.1)',
    borderLeftColor: '#000',
    borderRadius: '50%',
    width: '100px',
    height: '100px',
    animation: 'spin 1s linear infinite',
  }

  // Keyframes for spinner animation
  const spinnerKeyframes = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    @media (max-width: 768px) {
      .bzfy-o-media-uploader-iframe-browser {
        border-radius: 0 !important;
      }
    }
  `

  return (
    <div data-testid="media-uploader-iframe">
      <style>{spinnerKeyframes}</style>
      <div className="bzfy-o-popup-overlay bzfy-o-media-uploader-iframe-container" />
      <div className="bzfy-o-popup bzfy-o-media-uploader-iframe-container">
        {!isLoading && (
          <button
            className="bzfy-o-media-uploader-iframe-popup-close"
            onClick={handleCloseClick}
            type="button"
          >
            <Icon color="#000" icon="close" />
          </button>
        )}
        <div className="bzfy-o-media-uploader-iframe-iframe">
          {isLoading ? (
            <div style={spinnerStyle} />
          ) : (
            <iframe
              className="bzfy-o-media-uploader-iframe-browser"
              onLoad={(e) => sendPostMessage(e.target)}
              src={url}
              style={{ borderRadius: '4px' }}
              title="media-uploader-iframe"
            />
          )}
        </div>
      </div>
    </div>
  )
}

MediaUploaderIframe.propTypes = {
  feedKey: string.isRequired,
  handleCloseClick: func.isRequired,
  language: string.isRequired,
  settings: object,
  termsAndConditionsURL: string,
  privacyPolicyUrl: string,
}

export default MediaUploaderIframe
